import memoizeOne from 'memoize-one';

import i18n from 'Utilities/i18n';

interface Urls {
  lslApi: string;
  oauth: {
    consentUrl: string;
  };
}

/** Reference used to store urls */
export const urls: Urls = {
  lslApi: '',
  oauth: {
    consentUrl: '',
  },
};

/**
 * Sets the urls object to be ready for services
 * - config: object - The environment config
 */
export const setUrls = (config: Partial<Urls>): void => {
  const { lslApi, oauth } = config;

  Object.assign(urls, {
    lslApi,
    oauth: {
      consentUrl: oauth?.consentUrl || '',
    },
  });
};

export const getFromAccountType = memoizeOne((item, accountType?) => {
  if (!accountType) {
    return item.pat || item.default || {};
  }

  if (accountType === 'hcp') {
    accountType = 'pro';
  } else if (accountType === 'patient') {
    accountType = 'pat';
  }

  return item[accountType] || item.default || {};
});

export const convertMgToMmol = (value: string | number): number =>
  Number((Number(value) / 18).toFixed(1));

export const convertMmolToMg = (value: string | number): number =>
  Number((Number(value) * 18).toFixed(0));

interface secRuleChangeLock {
  code: 11;
  data?: {
    interval: number;
  };
}

interface secRuleGenerations {
  code: 14;
  data?: {
    limit: number;
  };
}

interface secRuleLockout {
  code: 60;
  data: {
    lockout: number;
  };
}

interface secRuleDefault {
  code: Exclude<number, 11 | 14 | 60>;
  data?: {
    limit: number;
  };
}

type secRuleError = secRuleChangeLock | secRuleGenerations | secRuleLockout;

export const isSecRuleError = (data: Record<string, any> = {}): data is secRuleError => !!data.code;

/**
 * Get the translated error message for the corresponding security rule error
 *
 * @param {*} secRuleErr Security rule error definition
 * @param {number} secRuleErr.code Rule code identifier
 * @param {*} secRuleErr.data Rule data context
 * @param {string} secRuleErr.message Rule brief message
 */
export const getSecurityErrorMessage = (msg: secRuleError): string => {
  let errMsg = '';

  switch (msg.code) {
    case 11: // Change lock
      // Interval is in seconds, error message expects hours
      errMsg = i18n.t('SecurityRules.' + msg.code, {
        count: (msg.data !== undefined ? msg.data.interval : 0) / 60 / 60,
      });
      break;

    case 14: // Generations
      // Limit of 0 means no reusable passwords
      errMsg = i18n.t(
        [
          'SecurityRules.' + msg.code + '_' + (msg.data && msg.data.limit),
          'SecurityRules.' + msg.code,
        ],
        { count: msg.data && msg.data.limit }
      );
      break;

    case 60: // Lockout time is in seconds, error message expects minutes
      errMsg = i18n.t('SecurityRules.' + msg.code, { count: msg.data.lockout / 60 });
      break;

    default:
      const d = msg as secRuleDefault;

      errMsg = i18n.t('SecurityRules.' + d.code, {
        count: d.data && d.data.limit,
      });
  }

  return errMsg;
};
